import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { FaChartBar, FaPen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { fetchBarExams, fetchBarExamStatistics } from "../api/barExam";

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined || value === "") return "-";

  const number = Number(value);
  if (Number.isNaN(number)) return value;

  return number.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const getExamMonth = (exam) => {
  const text = [exam?.title, exam?.name, exam?.exam_round]
    .filter(Boolean)
    .join(" ");
  const monthMatch = text.match(/(\d{1,2})\s*월/);
  const shortMatch = text.match(/(\d{1,2})\s*모/);

  if (monthMatch) return Number(monthMatch[1]);
  if (shortMatch) return Number(shortMatch[1]);

  const round = Number(exam?.exam_round);
  return Number.isNaN(round) ? null : round;
};

const getExamSortScore = (exam) => {
  const year = Number(exam?.exam_year) || 0;
  const month = getExamMonth(exam) || 0;

  return year * 100 + month;
};

const BarExamPromoBanner = () => {
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [isLoadingExams, setIsLoadingExams] = useState(true);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [examError, setExamError] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadPreview = async () => {
      try {
        const examsData = await fetchBarExams();
        const examList = Array.isArray(examsData)
          ? examsData
          : examsData?.results || [];
        const defaultExam =
          [...examList].sort((a, b) => getExamSortScore(b) - getExamSortScore(a))[0] ||
          null;

        if (!isMounted) return;
        setExam(defaultExam || null);
        setIsLoadingExams(false);

        if (!defaultExam?.id) return;

        setIsLoadingStatistics(true);
        try {
          const data = await fetchBarExamStatistics(defaultExam.id);
          if (isMounted) setStatistics(data || null);
        } catch (error) {
          console.error("Error fetching bar exam statistics preview:", error);
        } finally {
          if (isMounted) setIsLoadingStatistics(false);
        }
      } catch (error) {
        console.error("Error fetching bar exams for promotion:", error);
        if (isMounted) {
          setExamError(true);
          setIsLoadingExams(false);
        }
      }
    };

    loadPreview();
    return () => {
      isMounted = false;
    };
  }, []);

  const hardestAreas = useMemo(
    () =>
      [...(statistics?.main_area_statistics || [])]
        .sort((a, b) => Number(a.correct_rate) - Number(b.correct_rate))
        .slice(0, 5),
    [statistics],
  );

  const summary = statistics?.summary;
  const wrongRateTop5 = (statistics?.wrong_rate_top5 || []).slice(0, 5);
  const scoreBands = (statistics?.score_band_statistics || []).filter((row) => {
    const bandStart = Number(row.band_start ?? String(row.band).split("-")[0]);
    return !Number.isNaN(bandStart) && bandStart >= 70;
  });
  const hasStatistics = Boolean(
    summary || wrongRateTop5.length || scoreBands.length || hardestAreas.length,
  );
  const hasExam = Boolean(exam?.id);
  const examTitle = exam?.title || exam?.name || "객관식 모의고사";
  const slideCount = hasStatistics ? 3 : 1;

  useEffect(() => {
    if (!hasStatistics) return undefined;

    const intervalId = window.setInterval(() => {
      setCurrentSlide((current) => (current + 1) % 3);
    }, 50000);

    return () => window.clearInterval(intervalId);
  }, [hasStatistics]);

  const moveSlide = (direction) => {
    setCurrentSlide(
      (current) => (current + direction + slideCount) % slideCount,
    );
  };

  return (
    <Box
      as="section"
      aria-roledescription="carousel"
      aria-label="변호사시험 모의고사 분석 안내"
      width="full"
      maxW="600px"
      mb="5"
      rounded="xl"
      shadow="lg"
      overflow="hidden"
      bg="white"
      position="relative"
    >
      <Box
        p={{ base: 5, md: 6 }}
        bgGradient="linear(to-br, blue.700, teal.500)"
        color="white"
      >
        <Badge colorScheme="yellow" color="gray.800" mb="2">
          변호사시험 모의고사 분석
        </Badge>
        <Heading as="h2" size="md" mb="2">
          {isLoadingExams
            ? "객관식 풀서비스로 채점부터 취약점까지 확인하세요"
            : `${examTitle} 풀서비스`}
        </Heading>
        <Text color="whiteAlpha.900" fontSize="sm" mb="4">
          오답률 TOP 문제, 점수대별 평균, 영역별 취약점까지 한 번에 확인할 수
          있습니다.
        </Text>
        <Flex gap="2" direction={{ base: "column", sm: "row" }}>
          <Button
            size="sm"
            leftIcon={<FaPen />}
            colorScheme="yellow"
            color="gray.800"
            isDisabled={!hasExam || isLoadingExams}
            onClick={() => navigate(`/bar-exam/${exam.id}/answer`)}
          >
            {isLoadingExams
              ? "시험 확인 중"
              : hasExam
                ? "답안 입력하기"
                : "준비 중"}
          </Button>
          <Button
            size="sm"
            leftIcon={<FaChartBar />}
            rightIcon={<ArrowForwardIcon />}
            bg="whiteAlpha.300"
            color="white"
            _hover={{ bg: "whiteAlpha.400" }}
            isDisabled={!hasExam || isLoadingExams}
            onClick={() => navigate(`/bar-exam/${exam.id}/statistics`)}
          >
            통계 보기
          </Button>
        </Flex>
        <Text fontSize="xs" color="whiteAlpha.800" mt="3">
          유효 제출자 기준 통계 · 제출 후 수정 1회 가능
        </Text>
      </Box>

      <Box minH={{ base: "270px", sm: "205px" }} position="relative" pb="8">
        {isLoadingExams || isLoadingStatistics ? (
          <SimpleGrid columns={{ base: 2, sm: 5 }} spacing="2" p="4">
            {[0, 1, 2, 3, 4].map((item) => (
              <Skeleton key={item} height="55px" rounded="md" />
            ))}
          </SimpleGrid>
        ) : !hasExam ? (
          <Box p="4">
            <Alert status={examError ? "warning" : "info"} rounded="md">
              <AlertIcon />
              현재 공개된 변호사시험 모의고사가 없습니다.
            </Alert>
          </Box>
        ) : !hasStatistics ? (
          <Box p="4">
            <Alert status="info" rounded="md">
              <AlertIcon />
              아직 충분한 통계가 쌓이지 않았습니다.
            </Alert>
          </Box>
        ) : currentSlide === 0 ? (
          <Box p={{ base: 4, md: 5 }}>
            <Text fontWeight="bold" fontSize="sm" mb="2">
              오답률 TOP 5
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="2">
              {wrongRateTop5.map((row, index) => (
                <Flex
                  key={`${row.subject_code}-${row.number}-${index}`}
                  align="center"
                  justify="space-between"
                  bg="red.50"
                  rounded="md"
                  px="3"
                  py="2"
                  fontSize="xs"
                >
                  <Text fontWeight="bold" noOfLines={1}>
                    {row.subject} {row.number}번
                  </Text>
                  <HStack spacing="2">
                    <Text color="gray.500">
                      정답 {row.correct_choice ?? "-"}번
                    </Text>
                    <Text color="red.600" fontWeight="bold">
                      오답 {formatNumber(row.wrong_rate, 1)}%
                    </Text>
                  </HStack>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        ) : currentSlide === 1 ? (
          <Box p={{ base: 4, md: 5 }}>
            <Heading as="h3" size="sm" mb="3">
              점수대별 과목 평균
            </Heading>
            <Box maxH="145px" overflowY="auto" pr="2">
              {scoreBands.map((row, index) => (
                <Box
                  key={`${row.band}-${index}`}
                  borderBottomWidth="1px"
                  py="2"
                >
                  <Flex justify="space-between" fontSize="xs" mb="1">
                    <Text fontWeight="bold">
                      {row.band} · {formatNumber(row.count)}명
                    </Text>
                    <Text color="blue.600" fontWeight="bold">
                      총점 {formatNumber(row.avg_total_score, 1)}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.500">
                    공법 {formatNumber(row.avg_public_law_score, 1)} · 형사법{" "}
                    {formatNumber(row.avg_criminal_law_score, 1)} · 민사법{" "}
                    {formatNumber(row.avg_civil_law_score, 1)}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box p={{ base: 4, md: 5 }}>
            <Heading as="h3" size="sm" mb="3">
              정답률이 낮은 영역 TOP 5
            </Heading>
            {hardestAreas.map((row, index) => (
              <Flex
                key={`${row.subject_code}-${row.main_area}-${index}`}
                justify="space-between"
                borderBottomWidth="1px"
                py="1.5"
                fontSize="xs"
              >
                <Text fontWeight="bold" noOfLines={1}>
                  {row.subject} · {row.main_area}
                </Text>
                <HStack spacing="2">
                  <Text color="green.600">
                    정답 {formatNumber(row.correct_rate, 1)}%
                  </Text>
                  <Text color="red.500">
                    오답 {formatNumber(row.wrong_rate, 1)}%
                  </Text>
                </HStack>
              </Flex>
            ))}
          </Box>
        )}

        {slideCount > 1 && (
          <Flex
            align="center"
            justify="center"
            gap="2"
            position="absolute"
            bottom="1"
            left="0"
            right="0"
          >
            <IconButton
              aria-label="이전 배너"
              icon={<ChevronLeftIcon />}
              size="xs"
              variant="ghost"
              color="gray.600"
              onClick={() => moveSlide(-1)}
            />
            <HStack spacing="1.5">
              {Array.from({ length: slideCount }).map((_, index) => (
                <Box
                  as="button"
                  key={index}
                  aria-label={`${index + 1}번 배너로 이동`}
                  aria-current={currentSlide === index ? "true" : undefined}
                  w={currentSlide === index ? "16px" : "6px"}
                  h="6px"
                  rounded="full"
                  bg={currentSlide === index ? "teal.300" : "gray.300"}
                  transition="all 0.2s"
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </HStack>
            <IconButton
              aria-label="다음 배너"
              icon={<ChevronRightIcon />}
              size="xs"
              variant="ghost"
              color="gray.600"
              onClick={() => moveSlide(1)}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default BarExamPromoBanner;
